import { decryptJson, encryptJson } from '@circa/shared/crypto';
import type { TripsPairResponse, TripsSpan, TripsWhereResponse } from '@circa/shared/protocol';
import { useSyncExternalStore } from 'react';
import { api } from '@/core/api';
import { deleteMeta, getMeta, setMeta } from '@/core/db';
import { keys } from '@/core/keys';
import { getSetting, setSetting, useSetting } from '@/core/settings';

// Trips App: your flights, hotels and other bookings, each with its time zone. After Trace,
// it's how Sleep knows where you were. The bookings are kept on this device, encrypted (like
// the Trace copy); the link (address + device token) is an encrypted, synced setting.

export const TRIPS_ORIGIN = 'https://tripsapp.app';
export const TRIPS_KEY = 'conn.trips';
const CACHE_KEY = 'trips.cache';

export interface TripsLink {
  origin: string;
  token: string;
  deviceId: string;
  connectedAt: number;
  lastSync?: number;
  error?: string;
}

export const getTrips = () => getSetting<TripsLink | null>(TRIPS_KEY, null);
export const useTripsLink = () => useSetting<TripsLink | null>(TRIPS_KEY, null)[0];

let spans: TripsSpan[] | null = null;
let version = 0;
const listeners = new Set<() => void>();
function emit() {
  version++;
  for (const l of listeners) l();
}

/** Re-renders when the bookings change (after a pull or loading from disk). */
export function useTripsVersion(): number {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => version,
  );
}

export const peekTrips = (): TripsSpan[] => spans ?? [];

export async function loadTrips(): Promise<TripsSpan[]> {
  if (spans) return spans;
  const bytes = await getMeta<Uint8Array>(CACHE_KEY);
  if (!bytes) return [];
  try {
    spans = await decryptJson<TripsSpan[]>(keys.dataKey(), bytes);
    emit();
  } catch {
    spans = null;
  }
  return spans ?? [];
}

export async function pairTrips(code: string, origin = TRIPS_ORIGIN): Promise<number> {
  const base = origin.trim().replace(/\/+$/, '');
  const j = await api<TripsPairResponse>('/api/trips/pair', {
    method: 'POST',
    body: { origin: base, code: code.trim().toUpperCase() },
  });
  setSetting(TRIPS_KEY, {
    origin: base,
    token: j.token,
    deviceId: j.deviceId,
    connectedAt: Date.now(),
  } satisfies TripsLink);
  return syncTrips();
}

export async function disconnectTrips() {
  setSetting(TRIPS_KEY, null);
  spans = null;
  await deleteMeta(CACHE_KEY);
  emit();
}

/** Fetch every booking again (a small set) and keep the ones with a zone. */
export async function syncTrips(): Promise<number> {
  const link = getTrips();
  if (!link) return 0;
  try {
    const j = await api<TripsWhereResponse>('/api/trips/where', {
      method: 'POST',
      body: { origin: link.origin, token: link.token },
    });
    const before = JSON.stringify(spans ?? []);
    spans = j.spans;
    await setMeta(CACHE_KEY, await encryptJson(keys.dataKey(), spans));
    setSetting(TRIPS_KEY, { ...link, lastSync: Date.now(), error: undefined });
    if (before !== JSON.stringify(spans)) emit();
    return spans.length;
  } catch (err) {
    setSetting(TRIPS_KEY, { ...link, error: (err as Error).message });
    throw err;
  }
}

/** Replace the local copy (development demo data). */
export async function setTripsSpans(next: TripsSpan[]) {
  spans = next;
  await setMeta(CACHE_KEY, await encryptJson(keys.dataKey(), spans));
  emit();
}

// ------------------------------------------------------------------ where you said you were

/** Days you told the app where you were: "YYYY-MM-DD" → IANA zone. Synced, encrypted. */
export const PLACES_KEY = 'whereabouts';

export const getDayZones = () => getSetting<Record<string, string>>(PLACES_KEY, {});

export function useDayZones(): [Record<string, string>, (day: string, tz: string | null) => void] {
  const [map, set] = useSetting<Record<string, string>>(PLACES_KEY, {});
  return [
    map,
    (day, tz) => {
      const next = { ...map };
      if (tz) next[day] = tz;
      else delete next[day];
      set(next);
    },
  ];
}

export function setDayZones(patch: Record<string, string>) {
  setSetting(PLACES_KEY, { ...getDayZones(), ...patch });
}
